/**
 * TopBar Component
 *
 * Features:
 * - Dynamic page title based on current route/state
 * - User avatar and name
 * - Logout button
 */
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';

export const TopBar = () => {
  const { user, firebaseUser, logout } = useAuth();
  const { state } = useApp();
  const location = useLocation();

  // Determine page title based on current route
  const getPageTitle = (): string => {
    if (location.pathname.startsWith('/admin')) {
      return 'Admin Panel';
    }
    // On reports page, show current report name
    if (state.currentReport) {
      return state.currentReport.name;
    }
    return 'Reports';
  };

  // Get display name and avatar
  const displayName = user ? `${user.firstName} ${user.lastName}`.trim() || firebaseUser?.displayName || 'User' : firebaseUser?.displayName || 'User';
  const avatarUrl = firebaseUser?.photoURL;
  const email = user?.email || firebaseUser?.email || '';

  const handleLogout = async () => {
    await logout();
  };

  return (
    <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-end px-6 flex-shrink-0 relative">
      {/* Center - Title (absolutely positioned for true centering) */}
      <div className="absolute left-0 right-0 flex justify-center pointer-events-none">
        <h1 className="text-2xl font-semibold text-gray-900">{getPageTitle()}</h1>
      </div>

      {/* Right side - User info */}
      <div className="flex items-center gap-4 relative z-10">
        {/* User Info */}
        <div className="flex items-center gap-3">
          {/* Avatar */}
          {avatarUrl ? (
            <img
              src={avatarUrl}
              alt={displayName}
              className="w-8 h-8 rounded-full"
              referrerPolicy="no-referrer"
            />
          ) : (
            <div className="w-8 h-8 rounded-full bg-metrica-green text-white flex items-center justify-center text-sm font-medium">
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}

          {/* Name & Email */}
          <div className="hidden sm:block">
            <p className="text-sm font-medium text-gray-900 leading-tight">
              {displayName}
            </p>
            <p className="text-xs text-gray-500 leading-tight">{email}</p>
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          title="Logout"
        >
          <LogoutIcon />
          <span className="hidden sm:inline">Logout</span>
        </button>
      </div>
    </header>
  );
};

/**
 * Logout icon
 */
const LogoutIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
);

export default TopBar;
